import { formatCurrency } from '@/utils/function'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button, Select, Typogaphy } from 'vnetwork-ui'
import { getCustomerCredits } from '../../services'
import { useState, useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import AddIcons from '@/icons/AddIcons'
import EditIcons from '@/icons/EditIcons'

const CreditSummaryCard = () => {
    const { id } = useParams()
    const { t } = useTranslation()
    const { setModal, open } = useModal()
    const [creditSelect, setCreditSelected] = useState(0)

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS_CREDITS, Number(id)],
        queryFn: () => getCustomerCredits({ customerID: Number(id) }),
    })

    // Calculate totals based on selected credit
    const { remainingAmount, totalAmount, usedAmount, percentage } =
        useMemo(() => {
            if (!data?.results)
                return {
                    remainingAmount: 0,
                    totalAmount: 0,
                    usedAmount: 0,
                    percentage: 0,
                }

            const remaining = data.results.reduce((total, credit) => {
                if (creditSelect !== 0)
                    return credit.credit_id === creditSelect
                        ? credit.credit_remaining
                        : 0
                return total + credit.credit_remaining
            }, 0)

            const total = data.results.reduce((total, credit) => {
                if (creditSelect !== 0)
                    return credit.credit_id === creditSelect
                        ? credit.credit_total
                        : 0
                return total + credit.credit_total
            }, 0)

            const used = data.results.reduce((total, credit) => {
                if (creditSelect !== 0)
                    return credit.credit_id === creditSelect
                        ? credit.credit_used
                        : 0
                return total + credit.credit_used
            }, 0)

            return {
                remainingAmount: remaining,
                totalAmount: total,
                usedAmount: used,
                percentage: total > 0 ? (remaining / total) * 100 : 0,
            }
        }, [data?.results, creditSelect])

    const selectedCredit =
        creditSelect === 0
            ? undefined
            : data?.results?.find((c) => c.credit_id === creditSelect)

    const stateComponent = renderState()
    if (stateComponent) return <div className="mb-4">{stateComponent}</div>

    // Empty state when no credits - compact UI
    if (!data?.results?.length) return null

    return (
        <div data-tour="credit-summary" className="mb-2">
            <div className="grid grid-cols-1 lg:grid-cols-4 md:grid-cols-2 gap-4">
                {/* Remaining / Total */}
                <div
                    data-tour="credit-remaining"
                    className="bg-gray-f8 p-4 rounded-[8px]"
                >
                    <div className="flex items-center gap-2 text-gray-500">
                        <Typogaphy
                            size="small"
                            className="flex items-center gap-1"
                        >
                            {t('customers.credits.transactions.remaining')}
                        </Typogaphy>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                            {percentage.toFixed(0)}%
                        </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <Typogaphy
                            size="large"
                            className="font-semibold text-c-success"
                        >
                            {formatCurrency(remainingAmount)}
                        </Typogaphy>
                        <span className="text-gray-400">/</span>
                        <Typogaphy
                            size="large"
                            className="font-semibold text-gray-600"
                        >
                            {formatCurrency(totalAmount)}
                        </Typogaphy>
                    </div>
                </div>

                {/* Used */}
                <div
                    data-tour="credit-used"
                    className="bg-gray-f8 p-4 rounded-[8px]"
                >
                    <Typogaphy size="small" className="text-gray-500">
                        {t('customers.credits.transactions.used')}
                    </Typogaphy>
                    <Typogaphy size="large" className="font-semibold mt-2">
                        {formatCurrency(usedAmount)}
                    </Typogaphy>
                </div>

                {/* Wallet Select */}
                <div
                    data-tour="credit-wallet-select"
                    className="bg-gray-f8 p-4 rounded-[8px]"
                >
                    <Typogaphy size="small" className="text-gray-500 mb-2">
                        {t('customers.credits.transactions.choose')}
                    </Typogaphy>
                    <Select
                        size="small"
                        onChange={(value) => setCreditSelected(value as number)}
                        value={creditSelect}
                        options={[{ value: 0, label: t('all') }].concat(
                            data?.results?.map((credit) => ({
                                value: credit.credit_id,
                                label: credit.credit_name,
                            })) || []
                        )}
                        placeholder={t('customers.credits.transactions.choose')}
                    />
                </div>

                {/* Actions */}
                <div
                    data-tour="credit-actions"
                    className="bg-gray-f8 p-4 rounded-[8px] space-y-2"
                >
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcons />}
                        disabled={open}
                        className="w-full"
                        onClick={() =>
                            setModal('CreditModals/CreateCreditModal/index', {
                                customerID: Number(id),
                                creditID: creditSelect,
                            })
                        }
                    >
                        {t('credit.transaction.create')}
                    </Button>
                    {creditSelect !== 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcons />}
                            disabled={open}
                            className="w-full"
                            onClick={() =>
                                setModal(
                                    'CreditModals/UpdateCreditModal/index',
                                    {
                                        customerID: Number(id),
                                        creditID: creditSelect,
                                        credit: selectedCredit,
                                    }
                                )
                            }
                        >
                            {t('credit.transaction.edit')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CreditSummaryCard
